import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import PropTypes from "prop-types";
import "chart.js/auto";

const LineChartComponent = ({ prf_summary, selectedYear, onYearChange }) => {
  const jalaliMonths = [
    "فروردین",
    "اردیبهشت",
    "خرداد",
    "تیر",
    "مرداد",
    "شهریور",
    "مهر",
    "آبان",
    "آذر",
    "دی",
    "بهمن",
    "اسفند",
  ];

  const [totalSum, setTotalSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [dataType, setDataType] = useState("price");

  // Helper for axis numbers
  const formatAxisNumber = (value) => {
    if (value === null || value === undefined) return "";
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
      const v = (value / 1_000_000).toFixed(1).replace(/\.0$/, "");
      return `${v}m`;
    }
    if (abs >= 1_000) {
      const v = (value / 1_000).toFixed(1).replace(/\.0$/, "");
      return `${v}k`;
    }
    return value.toLocaleString("en-US");
  };

  useEffect(() => {
    if (prf_summary) {
      if (selectedYear === "all" && prf_summary.yearly_data) {
        const sum = prf_summary.yearly_data.reduce(
          (acc, item) => acc + parseFloat(item.total_price),
          0
        );
        const count = prf_summary.yearly_data.reduce(
          (acc, item) => acc + parseInt(item.count, 10),
          0
        );
        setTotalSum(sum);
        setTotalCount(count);
      } else if (prf_summary.monthly_data) {
        const sum = prf_summary.monthly_data.reduce(
          (acc, item) => acc + parseFloat(item.total_price),
          0
        );
        const count = prf_summary.monthly_data.reduce(
          (acc, item) => acc + parseInt(item.count, 10),
          0
        );
        setTotalSum(sum);
        setTotalCount(count);
      } else {
        setTotalSum(0);
        setTotalCount(0);
      }
    }
  }, [prf_summary, selectedYear]);

  const buildChartData = () => {
    if (!prf_summary) return { labels: [], datasets: [] };

    if (selectedYear === "all" && prf_summary.yearly_data) {
      const labels = prf_summary.yearly_data.map((item) => item.year);
      const dataPoints = prf_summary.yearly_data.map((item) =>
        dataType === "price" ? parseFloat(item.total_price) : item.count
      );

      return {
        labels,
        datasets: [
          {
            label:
              dataType === "price"
                ? "مجموع قیمت ثبت سفارش به تفکیک سال"
                : "تعداد ثبت سفارش‌ها به تفکیک سال",
            data: dataPoints,
            borderColor:
              dataType === "price"
                ? "rgba(56, 189, 248, 1)"
                : "rgba(249, 115, 22, 1)",
            backgroundColor:
              dataType === "price"
                ? "rgba(56, 189, 248, 0.25)"
                : "rgba(249, 115, 22, 0.25)",
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: "#ffffff",
            pointBorderWidth: 2,
          },
        ],
      };
    }

    if (prf_summary.monthly_data) {
      const monthlyValues = Array(12).fill(0);
      prf_summary.monthly_data.forEach((item) => {
        const idx = item.month - 1;
        if (idx >= 0 && idx < 12) {
          monthlyValues[idx] =
            dataType === "price" ? parseFloat(item.total_price) : item.count;
        }
      });

      return {
        labels: jalaliMonths,
        datasets: [
          {
            label:
              dataType === "price"
                ? `مجموع قیمت در سال ${selectedYear}`
                : `تعداد ثبت سفارش‌ها در سال ${selectedYear}`,
            data: monthlyValues,
            borderColor:
              dataType === "price"
                ? "rgba(129, 140, 248, 1)"
                : "rgba(59, 130, 246, 1)",
            backgroundColor:
              dataType === "price"
                ? "rgba(129, 140, 248, 0.25)"
                : "rgba(59, 130, 246, 0.25)",
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: "#ffffff",
            pointBorderWidth: 2,
          },
        ],
      };
    }

    return { labels: [], datasets: [] };
  };

  const chartData = buildChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 10,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) label += ": ";
            if (context.parsed.y !== null) {
              if (dataType === "price") {
                // full value in tooltip
                label += context.parsed.y.toLocaleString("en-US") + " یورو";
              } else {
                label += context.parsed.y.toLocaleString("en-US") + " عدد";
              }
            }
            return label;
          },
        },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          font: { size: 12 },
          usePointStyle: true,
          padding: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(148, 163, 184, 0.25)",
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return formatAxisNumber(value);
          },
        },
        title: {
          display: true,
          text: dataType === "price" ? "مبلغ (یورو)" : "تعداد ثبت سفارش",
          font: { size: 13, weight: "600" },
          color: "#334155",
        },
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } },
        title: {
          display: true,
          text: selectedYear === "all" ? "سال" : "ماه",
          font: { size: 13, weight: "600" },
          color: "#334155",
        },
      },
    },
  };

  return (
    <div
      dir="rtl"
      className="flex flex-col h-full min-h-[280px] rounded-xl bg-gradient-to-b from-slate-50 to-white"
    >
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-700">
          <span className="font-medium">سال:</span>
          <select
            id="year-selector"
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            className="px-2 py-1 rounded-md border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
          >
            <option value="all">همه سال‌ها</option>
            {prf_summary?.yearly_data?.map((item) => (
              <option key={item.year} value={item.year}>
                {item.year}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <span className="text-slate-600">نمایش:</span>
          <div className="inline-flex rounded-full bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setDataType("price")}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                dataType === "price"
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-200"
              }`}
            >
              ارزش
            </button>
            <button
              type="button"
              onClick={() => setDataType("count")}
              className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium transition-all ${
                dataType === "count"
                  ? "bg-indigo-500 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-200"
              }`}
            >
              تعداد
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="relative flex-1 min-h-[200px] sm:min-h-[260px] md:min-h-[320px]">
        <div className="absolute inset-0">
          {chartData.labels.length > 0 ? (
            <Line data={chartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-slate-500">
              داده‌ای برای نمایش وجود ندارد.
            </div>
          )}
        </div>
      </div>

      {/* Summary footer */}
      <div className="mt-3 pt-2 border-t border-slate-100 text-xs sm:text-sm font-semibold text-slate-700 flex items-center justify-between gap-2">
        <span className="truncate">
          {dataType === "price"
            ? `مجموع قیمت: ${totalSum.toLocaleString("en-US")} یورو`
            : `تعداد ثبت سفارش‌ها: ${totalCount.toLocaleString("en-US")} عدد`}
        </span>
        {selectedYear !== "all" && (
          <span className="text-[0.7rem] sm:text-xs text-slate-400">
            سال انتخاب‌شده: {selectedYear}
          </span>
        )}
      </div>
    </div>
  );
};

LineChartComponent.propTypes = {
  prf_summary: PropTypes.shape({
    yearly_data: PropTypes.arrayOf(
      PropTypes.shape({
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number.isRequired,
      })
    ),
    monthly_data: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.number.isRequired,
        total_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        count: PropTypes.number.isRequired,
      })
    ),
  }),
  selectedYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onYearChange: PropTypes.func.isRequired,
};

export default LineChartComponent;
